import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth/auth-context'
import { hasSeenWelcome } from '@/lib/utils/welcome-seen'
import { BiscuitSkeleton } from '@/components/biscuit-skeleton'

interface WelcomeGuardProps {
  children: React.ReactNode
}

/**
 * WelcomeGuard ensures unauthenticated users see the welcome screen
 * on their first visit, regardless of what URL they navigate to.
 *
 * Authenticated users skip the welcome check entirely.
 */
export function WelcomeGuard({ children }: WelcomeGuardProps) {
  const { user, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    // Wait for auth to resolve before making decisions
    if (loading) return

    // Skip welcome check for authenticated users
    if (user) return

    // Skip if already on welcome page (prevent redirect loop)
    if (location.pathname === '/welcome') return

    // The public marketing landing at "/" is now the first-run intro, so don't
    // bounce guests off it into the legacy welcome carousel.
    if (location.pathname === '/') return

    // For unauthenticated users: check if they've seen welcome
    // Honor both the legacy moneybees flag and the new welcome-seen util flag.
    const seenLegacy = localStorage.getItem('moneybees_welcome_shown') === 'true'
    const seenNew = hasSeenWelcome()

    if (!seenLegacy && !seenNew) {
      navigate('/welcome', { replace: true })
    }

  }, [user, loading, location.pathname, navigate])

  // Show skeleton while auth is loading. Match the app-shell Suspense fallback's
  // centering wrapper so the skeleton doesn't jump position when auth resolves.
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-8">
        <BiscuitSkeleton />
      </div>
    )
  }

  return <>{children}</>
}
